import { apiDownload } from '@/api/client';
import type { OrderListParams } from '@/api/endpoints/orders';

/** Builds a collision-resistant CSV filename for orders export downloads. */
export function buildOrdersExportFilename(now: Date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  const month = pad(now.getMonth() + 1);
  const day = pad(now.getDate());
  const year = now.getFullYear();

  let hours12 = now.getHours() % 12;
  if (hours12 === 0) hours12 = 12;
  const amPm = now.getHours() >= 12 ? 'PM' : 'AM';
  const time = `${pad(hours12)}${pad(now.getMinutes())}${pad(now.getSeconds())}${amPm}`;

  return `orders-export-${month}-${day}-${year}-${time}.csv`;
}

/** Progress snapshot emitted after export completes. */
export type OrdersCsvExportProgress = {
  rowsExported: number;
  estimatedTotal: number | null;
  chunkIndex: number;
  elapsedMs: number;
};

type RunOrdersCsvExportOptions = {
  estimatedTotal?: number | null;
  onProgress?: (progress: OrdersCsvExportProgress) => void;
};

function buildExportQuery(params: OrderListParams): string {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const entries = Object.entries({ ...params, timeZone }).filter(
    ([, value]) => value !== undefined && value !== null && value !== '',
  );
  if (entries.length === 0) return '';
  return `?${entries.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`).join('&')}`;
}

/**
 * Downloads the filtered orders set as CSV in one legacy-style request. The server runs a
 * single lean query and streams the file; the browser saves it when the response completes.
 */
export async function runOrdersCsvExport(
  params: OrderListParams,
  options: RunOrdersCsvExportOptions = {},
): Promise<void> {
  const filename = buildOrdersExportFilename();
  const startedAt = Date.now();
  const estimatedTotal = options.estimatedTotal ?? null;

  await apiDownload(`/orders/export${buildExportQuery(params)}`, filename);

  options.onProgress?.({
    rowsExported: estimatedTotal ?? 0,
    estimatedTotal,
    chunkIndex: 1,
    elapsedMs: Date.now() - startedAt,
  });
}

/** Kept for tests and docs — server-side chunked export cap when gateway limits apply. */
export const ORDER_EXPORT_CHUNK_MAX_ROWS = 5_000;
